// components/CoursePlanner/SemesterGrid.tsx
import { memo } from 'react';
import { ChevronDown, ChevronRight, Plus, BookOpen, Calendar, Sparkles, ArrowRight, Settings, Trash2, Award } from 'lucide-react';
import CourseCard from '../shared/CourseCard';
import { dispatchAskChatbotHelp } from '../../utils/appEvents';
import SemesterHeaderMenu from './SemesterHeaderMenu';
import PLCCapIndicator from './PLCCapIndicator';
import { isSpecialSemester, calculateTotalCredits } from '../../utils/courseUtils';
import WorkloadIndicator from './WorkloadIndicator';
import {
  sortSemesterEntries,
  getSemesterDisplayName,
  isAPCreditsSemester,
  isOtherCreditsSemester,
  isPriorCreditsSemester,
  toBackendSemesterCode,
} from '../../utils/semesterUtils';
import { SemesterSkeleton } from '../shared/CourseCardSkeleton';
import {
  DraggableCourse,
  DropPlaceholder,
  SemesterDropZone,
  useDragDrop,
} from '../../contexts/DragDropContext';
import { useSuggestions } from '../../contexts/SuggestionContext';
import { usePlanOperations } from '../../contexts/PlanOperationsContext';
import { useModalOperations } from '../../contexts/ModalOperationsContext';
import { useCreditUnit, useInstitutionConfig } from '../../contexts/InstitutionConfigContext';
import type { AcademicPlan, EnrolledCourse, PrereqWarning, RetakeCandidate } from '../../types';

// UI Phase 2.2b: season identity moved from paint-bucket header backgrounds
// to a small colored dot; headers share one calm warm surface.
const SEASON_DOTS: Record<string, string> = {
  fall: 'bg-orange-400',
  spring: 'bg-green-500',
  winter: 'bg-blue-400',
  summer: 'bg-yellow-400',
};
// Tier 3.5b: a whisper of season color in the header wash (works over both
// light and dark cards since it's an alpha tint of a mid-tone hue).
const SEASON_WASH: Record<string, string> = {
  fall: 'bg-orange-400/10 hover:bg-orange-400/15',
  spring: 'bg-green-500/10 hover:bg-green-500/15',
  winter: 'bg-blue-400/10 hover:bg-blue-400/15',
  summer: 'bg-yellow-400/10 hover:bg-yellow-400/15',
};
const HEADER_SURFACE = 'bg-surface-sunken/70 hover:bg-surface-sunken border-line-soft';

interface SemesterTypeFlags {
  isAPCredits: boolean;
  isOtherCredits: boolean;
}

export function getSeasonDotClass(semester: string, { isAPCredits, isOtherCredits }: SemesterTypeFlags): string {
  if (isAPCredits) return 'bg-red-400';
  if (isOtherCredits) return 'bg-indigo-400';
  const name = (getSemesterDisplayName(semester) ?? '').toLowerCase();
  for (const [season, color] of Object.entries(SEASON_DOTS)) {
    if (name.includes(season)) return color;
  }
  return 'bg-gray-400';
}

function getSeasonWashClass(semester: string, { isAPCredits, isOtherCredits }: SemesterTypeFlags): string {
  if (isAPCredits) return 'bg-red-400/10 hover:bg-red-400/15';
  if (isOtherCredits) return 'bg-indigo-400/10 hover:bg-indigo-400/15';
  const name = (getSemesterDisplayName(semester) ?? '').toLowerCase();
  for (const [season, wash] of Object.entries(SEASON_WASH)) {
    if (name.includes(season)) return wash;
  }
  return HEADER_SURFACE;
}

interface CourseAction {
  type: 'move' | 'remove';
}

function getCourseActions(course: EnrolledCourse, { isAPCredits, isOtherCredits }: SemesterTypeFlags): CourseAction[] {
  if (isAPCredits) return [];
  if (isOtherCredits) return [{ type: 'move' }, { type: 'remove' }];
  if (course.isPlaceholder) return [];
  return [{ type: 'move' }, { type: 'remove' }];
}

interface NoSemestersStateProps {
  onManageSemesters: () => void;
}

const NoSemestersState = memo(({ onManageSemesters }: NoSemestersStateProps) => {
  const handleChatClick = () => {
    dispatchAskChatbotHelp({
      message: "Help me set up semesters for my academic plan. Which semesters should I add?",
      action: 'setup_semesters',
    });
  };

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-lg border border-indigo-200 p-4 md:p-6">
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 md:h-14 md:w-14 rounded-full bg-indigo-100 mb-4">
          <Calendar className="h-6 w-6 md:h-7 md:w-7 text-indigo-600" />
        </div>

        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          No Semesters Added
        </h3>

        <p className="text-sm text-gray-600 mb-4">
          Add semesters via <button
            onClick={onManageSemesters}
            className="text-indigo-600 hover:text-indigo-700 font-medium hover:underline"
          >Academic Plan → Manage Semesters</button> or by chatting with the AI Assistant.
        </p>

        <button
          onClick={handleChatClick}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <Sparkles className="h-4 w-4" />
          <span>Chat with AI Assistant</span>
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
});
NoSemestersState.displayName = 'NoSemestersState';

interface EmptySemesterStateProps {
  semester: string;
  isPriorCredits: boolean;
  onAddPlaceholder?: ((semester?: string) => void) | null;
  onOpenAddCourse?: (semester: string, semesterName: string) => void;
  isDragOver: boolean;
}

const EmptySemesterState = memo(({ semester, isPriorCredits, onOpenAddCourse, isDragOver }: EmptySemesterStateProps) => {
  const displayName = getSemesterDisplayName(semester) ?? semester;

  if (isDragOver) {
    return (
      <div className="text-center py-6 transition-all duration-200 text-primary">
        <BookOpen className="h-8 w-8 mx-auto mb-2 animate-bounce" />
        <p className="text-sm font-medium">Drop here</p>
      </div>
    );
  }

  return (
    <div className="text-center py-4 px-4">
      <button
        onClick={() => onOpenAddCourse && onOpenAddCourse(semester, displayName)}
        className="text-xs px-3 py-1.5 text-ink-2 hover:text-primary border border-dashed border-line-soft hover:border-primary rounded-control transition-colors flex items-center justify-center gap-1.5 mx-auto"
      >
        <Plus className="h-3.5 w-3.5" />
        {isPriorCredits ? 'Add credits' : 'Add course or drag here'}
      </button>
    </div>
  );
});
EmptySemesterState.displayName = 'EmptySemesterState';

interface SemesterGridProps {
  loading: boolean;
  academicPlan: AcademicPlan;
  collapsedSemesters: Record<string, boolean>;
  openDropdownId: string | null;
  setOpenDropdownId: (id: string | null) => void;
  dropdownContext?: string;
  currentSemester: string;
  /** Optional PLC cap override; when omitted, PLCCapIndicator reads the
   *  active institution's `priorLearningCap` (UX-R1 task 1.1). */
  plcCap?: number;
}

const SemesterGrid = ({
  loading,
  academicPlan,
  collapsedSemesters,
  openDropdownId,
  setOpenDropdownId,
  dropdownContext,
  currentSemester,
  plcCap,
}: SemesterGridProps) => {
  const { dragOverSemester, isDropPosition } = useDragDrop();
  const { config: institutionConfig } = useInstitutionConfig();
  const creditUnit = useCreditUnit();

  const {
    onAcceptSuggestion,
    onRejectSuggestion,
    onReassignGenEd,
    acceptingSuggestion,
    rejectingSuggestion,
    optimizationStatus,
  } = useSuggestions();

  const {
    onRemoveCourse,
    onMoveToSemester,
    onToggleSemester,
    onClearSemester,
    onDeleteSemester,
    onAddPlaceholder,
    onEditPlaceholder,
    onConvertPlaceholder,
    checkCourseAvailability,
  } = usePlanOperations();

  const {
    onOpenAddCourse,
    onOpenAPCredits,
    onAddOtherCredit,
    onManageSemesters,
    onUpdateGrade,
  } = useModalOperations();

  if (loading || !academicPlan || !academicPlan.semesters) {
    return (
      <div className="grid grid-cols-1 gap-4">
        {Array.from({ length: 4 }, (_, index) => (
          <SemesterSkeleton key={index} />
        ))}
      </div>
    );
  }

  // An empty AP Credits card is hidden where the institution offers no AP
  // credit (UNIC): it offered "Add AP test scores" for scores the school does
  // not accept. Plans loaded before the backend stopped creating the semester
  // there still carry it. A card that somehow holds courses stays visible.
  const sortedSemesters = sortSemesterEntries(Object.entries(academicPlan.semesters)).filter(
    ([semester, courses]) =>
      institutionConfig.supportsApCredits ||
      !isAPCreditsSemester(semester) ||
      (courses?.length ?? 0) > 0
  );

  const apCredits = academicPlan.semesters?.['000008'] || [];
  const otherCredits = academicPlan.semesters?.['000009'] || [];
  const apTotalCredits = calculateTotalCredits(apCredits);
  const otherTotalCredits = calculateTotalCredits(otherCredits);
  const hasPriorCredits = '000008' in academicPlan.semesters || '000009' in academicPlan.semesters;

  const regularSemesters = sortedSemesters.filter(([semester]) => !isPriorCreditsSemester(semester));
  if (regularSemesters.length === 0) {
    return <NoSemestersState onManageSemesters={onManageSemesters} />;
  }

  const prereqWarningMap: Record<string, PrereqWarning> = {};
  (academicPlan?.gpa?.prereq_warnings || []).forEach((w) => {
    prereqWarningMap[w.course_code] = w;
  });
  const retakeCandidateMap: Record<string, RetakeCandidate> = {};
  (academicPlan?.gpa?.retake_candidates || []).forEach((r) => {
    retakeCandidateMap[r.course_code] = r;
  });

  return (
    <div className="grid grid-cols-1 gap-4 print-course-schedule">
      <h2 className="print-section-title hidden print:block">Course Schedule by Semester</h2>

      {hasPriorCredits && (apCredits.length > 0 || otherCredits.length > 0) && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center justify-between print-hide">
          <span className="text-sm font-medium text-gray-700">Prior Learning Credits</span>
          <PLCCapIndicator
            apCredits={apTotalCredits}
            otherCredits={otherTotalCredits}
            cap={plcCap}
            showBreakdown={true}
          />
        </div>
      )}

      {optimizationStatus?.state === 'computing' && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-2.5 flex items-center gap-2 print-hide">
          <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" />
          <span className="text-sm text-purple-700">Updating suggestions...</span>
        </div>
      )}

      {sortedSemesters.map(([semester, courses]) => {
        const isSpecial = isSpecialSemester(semester);
        const isCollapsed = collapsedSemesters[semester];
        const isPriorCredits = isPriorCreditsSemester(semester);
        const isAPCredits = isAPCreditsSemester(semester);
        const isOtherCredits = isOtherCreditsSemester(semester);
        const isDragOver = dragOverSemester === semester;

        const displayName = getSemesterDisplayName(semester) ?? semester;

        const seasonDotClass = getSeasonDotClass(semester, { isAPCredits, isOtherCredits });
        const seasonWashClass = getSeasonWashClass(semester, { isAPCredits, isOtherCredits });

        return (
          <div key={semester} data-semester-anchor={semester} className="animate-fade-in print-semester">
            <div
              className={`border rounded-card bg-surface-card shadow-sm transition-all duration-200 ${
                isDragOver && courses.length === 0
                  ? 'border-primary bg-primary-50 shadow-lg scale-[1.02]'
                  : isDragOver
                  ? 'border-primary/60 bg-primary-50/50 shadow-lg'
                  : 'border-line-soft'
              } ${isCollapsed ? 'print:block' : ''}`}
            >
              <div className={`${seasonWashClass} border-b border-line-soft rounded-t-card p-3 flex items-start justify-between transition-all duration-200 print-semester-header`}>
                <div
                  className="flex-1 cursor-pointer"
                  onClick={() => onToggleSemester(semester)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${seasonDotClass}`}
                        aria-hidden="true"
                      />
                      <h3 className="font-semibold text-ink capitalize">
                        {displayName}
                      </h3>
                      {(() => {
                        const semesterGpa = academicPlan?.gpa?.semester_gpas?.[semester];
                        return semesterGpa != null ? (
                          <span className="text-xs font-medium text-ink-2 tabular-nums">
                            {semesterGpa.toFixed(2)} GPA
                          </span>
                        ) : null;
                      })()}
                    </div>
                    <div className="flex items-center space-x-3">
                    {isAPCredits ? (
                      onOpenAPCredits && (
                        <button
                          className="p-1 rounded hover:bg-black hover:bg-opacity-10 transition-colors print-hide"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenAPCredits();
                          }}
                          title="Manage AP Credits"
                        >
                          <Settings className="w-4 h-4 text-gray-600" />
                        </button>
                      )
                    ) : isOtherCredits ? (
                      onAddOtherCredit && (
                        <button
                          className="p-1 rounded hover:bg-black hover:bg-opacity-10 transition-colors print-hide"
                          onClick={(e) => {
                            e.stopPropagation();
                            onAddOtherCredit();
                          }}
                          title="Add Other Credit"
                        >
                          <Plus className="w-4 h-4 text-gray-600" />
                        </button>
                      )
                    ) : (
                      onOpenAddCourse && (
                        <button
                          className="p-1 rounded hover:bg-black hover:bg-opacity-10 transition-colors print-hide"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenAddCourse(semester, displayName);
                          }}
                          title={`Add course to ${displayName}`}
                        >
                          <Plus className="w-4 h-4 text-gray-600" />
                        </button>
                      )
                    )}
                    <button
                      className="p-1 rounded hover:bg-black hover:bg-opacity-10 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleSemester(semester);
                      }}
                    >
                      {isCollapsed ? (
                        <ChevronRight className="w-4 h-4 text-gray-600 transition-transform duration-200 print-hide" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-600 transition-transform duration-200 print-hide" />
                      )}
                    </button>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-gray-500 mt-0.5">
                    <span>{courses.length} {courses.length === 1 ? 'course' : 'courses'}</span>
                    <span>·</span>
                    {/* Deliberate seam (#205): this header INCLUDES placeholder
                        credits — a placeholder visibly occupies semester capacity
                        — while every server-side total (planned_credits, progress)
                        EXCLUDES them until Tier-1 matching lands. */}
                    <span>{creditUnit.format(calculateTotalCredits(courses))}</span>
                  </div>
                </div>
                <div
                  className="ml-2 print-hide"
                  onClick={(e) => e.stopPropagation()}
                >
                  <SemesterHeaderMenu
                    semester={semester}
                    onAddPlaceholder={isAPCredits ? null : onAddPlaceholder}
                    onClearSemester={onClearSemester}
                    courseCount={courses.length}
                    onDeleteSemester={onDeleteSemester}
                    canDelete={!isPriorCredits}
                    isCurrentSemester={
                      toBackendSemesterCode(semester) === toBackendSemesterCode(currentSemester)
                    }
                  />
                </div>
              </div>

              <SemesterDropZone
                zone={semester}
                disabled={isAPCredits}
                className={`transition-all duration-200 ${
                  courses.length === 0
                    ? isDragOver && !isAPCredits
                      ? isOtherCredits ? 'bg-indigo-50' : 'bg-primary-50'
                      : 'bg-gray-50'
                    : 'p-4'
                } ${isCollapsed ? 'hidden print:block' : ''} ${
                  isDragOver && isOtherCredits ? 'border-2 border-dashed border-indigo-300' : ''
                } print-semester-courses`}
              >
                {courses.length === 0 ? (
                  <div className="print-hide">
                    {isAPCredits ? (
                      // Speak the student's language: "Manage AP credits" in a
                      // ghost button read as settings furniture — an external
                      // tester never discovered AP support (UC Merced QA).
                      <div className="text-center py-4 px-4">
                        <button
                          onClick={onOpenAPCredits}
                          className="text-sm px-4 py-2 font-medium text-primary bg-primary/10 hover:bg-primary/15 border border-primary/30 rounded-control transition-colors flex items-center justify-center gap-1.5 mx-auto"
                        >
                          <Award className="h-4 w-4" />
                          Add AP test scores
                        </button>
                        <p className="mt-1.5 text-xs text-ink-3">
                          AP exams can satisfy degree requirements.
                        </p>
                      </div>
                    ) : isOtherCredits ? (
                      isDragOver ? (
                        <div className="text-center py-6 transition-all duration-200 text-indigo-700">
                          <BookOpen className="h-8 w-8 mx-auto mb-2 animate-bounce" />
                          <p className="text-sm font-medium">Drop here</p>
                        </div>
                      ) : (
                        <div className="text-center py-4 px-4">
                          <button
                            onClick={onAddOtherCredit}
                            className="text-xs px-3 py-1.5 text-gray-500 hover:text-indigo-700 border border-dashed border-gray-300 hover:border-indigo-400 rounded-md transition-colors flex items-center justify-center gap-1.5 mx-auto"
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Add credit or drag here
                          </button>
                        </div>
                      )
                    ) : (
                      <EmptySemesterState
                        semester={semester}
                        isPriorCredits={isPriorCredits}
                        onAddPlaceholder={onAddPlaceholder}
                        onOpenAddCourse={onOpenAddCourse}
                        isDragOver={isDragOver}
                      />
                    )}
                  </div>
                ) : (
                  <div className={`space-y-2 ${isSpecial ? 'min-h-[60px]' : 'min-h-[80px]'} print:min-h-0`}>
                    {isDragOver && !isAPCredits && (
                      <div className={`text-xs text-center py-2 font-medium animate-pulse print-hide ${
                        isOtherCredits ? 'text-indigo-700 bg-indigo-100 rounded-md border border-indigo-200' : 'text-primary'
                      }`}>
                        Drop to add course
                      </div>
                    )}

                    {courses.map((course, index) => (
                      <div key={`${semester}-${index}`} className="animate-fade-in print-course-item">
                        {!isAPCredits && isDropPosition(semester, index) && <DropPlaceholder />}

                        <span className="print:block hidden">
                          <span className="font-medium">{course.code} - {course.title}</span>
                        </span>
                        <span className="print:block hidden text-sm">
                          {creditUnit.format(course.credits)}
                        </span>

                        <div className="print:hidden">
                          <DraggableCourse
                            id={`${semester}::${index}`}
                            course={course}
                            from={semester}
                            index={index}
                            disabled={isAPCredits || !!course.isSuggestion}
                          >
                          {(dragBind) => (
                          <CourseCard
                            course={course}
                            variant="ultra-compact"
                            semester={semester}
                            index={index}
                            checkAvailability={isPriorCredits ? null : checkCourseAvailability}
                            courseRoles={(academicPlan as { courseRoles?: Record<string, string[]> })?.courseRoles?.[course.code]}

                            showCredits={true}
                            showDescription={false}
                            showBadges={['type', 'genEd', 'difficulty']}
                            showBadgeIcons={true}

                            actions={getCourseActions(course, { isAPCredits, isOtherCredits })}

                            customActions={isAPCredits ? [
                              {
                                icon: Trash2,
                                label: 'Remove (Manage AP Credits)',
                                action: 'manage_ap',
                              },
                            ] : undefined}

                            onAcceptSuggestion={onAcceptSuggestion}
                            onRejectSuggestion={onRejectSuggestion}
                            onReassignGenEd={onReassignGenEd}
                            isAcceptingSuggestion={acceptingSuggestion === course.code}
                            isRejectingSuggestion={rejectingSuggestion === course.code}

                            isDraggable={!isAPCredits && !course.isSuggestion}
                            dragBind={dragBind}
                            onAction={(actionType: string) => {
                              const handlers: Record<string, () => void> = {
                                manage_ap: () => { if (isAPCredits) onOpenAPCredits?.(); },
                                edit: () => { if (course.isPlaceholder) onEditPlaceholder(course); },
                                convert: () => { if (course.isPlaceholder) onConvertPlaceholder(course, semester, index); },
                                move: () => onMoveToSemester(course, semester, index),
                                remove: () => {
                                  if (isOtherCredits) {
                                    onRemoveCourse(semester, index, true);
                                    return;
                                  }
                                  if (course.isPlaceholder) {
                                    onRemoveCourse(semester, index);
                                    return;
                                  }
                                  const el = document.querySelector(`[data-course-key="${semester}-${index}"]`);
                                  if (el) {
                                    el.classList.add('animate-fade-out');
                                    setTimeout(() => onRemoveCourse(semester, index), 200);
                                  } else {
                                    onRemoveCourse(semester, index);
                                  }
                                },
                              };
                              handlers[actionType]?.();
                            }}

                            data-course-key={`${semester}-${index}`}

                            isDragging={dragBind.isDragging}
                            className={isAPCredits ? 'opacity-90 bg-gray-50' : undefined}

                            dropdownContext={dropdownContext}
                            contextId={`${semester}-${course.code || course}`}

                            openDropdownId={openDropdownId}
                            setOpenDropdownId={setOpenDropdownId}

                            showGradeInput={!isPriorCredits && !course.isSuggestion && !course.isPlaceholder}
                            grade={course.grade}
                            onGradeChange={(grade) => onUpdateGrade && onUpdateGrade(semester, course.code, grade)}

                            prereqWarning={prereqWarningMap[course.code]}
                            retakeCandidate={retakeCandidateMap[course.code]}
                          />
                          )}
                          </DraggableCourse>
                        </div>
                      </div>
                    ))}

                    {!isAPCredits && isDropPosition(semester, courses.length) && (
                      <DropPlaceholder />
                    )}

                    {!isPriorCredits && (
                      <WorkloadIndicator courses={courses} />
                    )}
                  </div>
                )}
              </SemesterDropZone>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SemesterGrid;
